import React, { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, Skeleton } from "@mui/material";
import useTheme from "../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../providers/ThemeProvider";
import ScrollableWrapper from "../../common/ScrollableWrapper";
import { HistoryScope } from "../useHistoryScope";
import MetricRow, { METRIC_ROW_HEIGHT, METRIC_ROW_MIN_HEIGHT } from "./MetricRow";
import ServiceLane from "./ServiceLane";
import { DEFAULT_LANE_ORDER, orderLanes, ServiceLaneKey } from "./serviceLanes";
import {
  readTimelinePreferences,
  writeTimelinePreferences,
} from "./timelinePreferences";
import TestsRow, { COMPACT_ROW_HEIGHT, TESTS_ROW_HEIGHT } from "./TestsRow";
import TimelineToolbar, {
  hasMetrics,
  MAX_TIMELINE_METRICS,
  TimelineView as TimelineViewMode,
} from "./TimelineToolbar";
import useTimelineEvents, { TEST_ROWS } from "./useTimelineEvents";
import useTimelineSeries from "./useTimelineSeries";
import {
  hasWearableGoal,
  parseSeriesKey,
  seriesKey,
} from "./timelineSeries";
import {
  getTimelineWindow,
  shiftAnchor,
  TimelinePeriod,
  xPct,
} from "./timeScale";
import classes from "./Timeline.module.css";

type Props = {
  scope: HistoryScope;
  /** The report with the PNOĒ metric scores is still loading. */
  metricsLoading?: boolean;
};

// Wide enough for a biomarker's "Normal range …" line on one row.
const LABEL_WIDTH = 240;
const AXIS_HEIGHT = 32;
// The metrics block keeps the height of a full selection whatever is
// selected, so the chart does not grow and shrink as metrics are toggled;
// the selected rows split it between them.
const METRICS_BLOCK_HEIGHT = MAX_TIMELINE_METRICS * METRIC_ROW_HEIGHT;
// Full screen never lays the card out narrower than this: below it the
// toolbar wraps and the axis labels collide. The dialog scrolls sideways.
const FULLSCREEN_MIN_WIDTH = 1100;
// ScrollableWrapper's reserved scrollbar gutter. In full screen the rows
// outside the scroller are inset by it, so their dates stay on the grid.
const SCROLL_GUTTER = 8;
// Opening selection: the most recently measured biomarkers, plus the wearable
// the dashboard chart used to open with.
const DEFAULT_BIOMARKERS = 3;
const DEFAULT_WEARABLE = seriesKey("wearable", "total-burn");

/**
 * History → Timeline (Figma 16499:64370 and its variants).
 *
 * - **Full timeline**: up to five metrics stacked one per row, each on its
 *   own scale with its range bands; a Tests row; one swimlane per service
 *   category.
 * - **Metrics only**: the same metric rows and date axis, without the tests
 *   and services below.
 * - **Events & Services**: no metrics; the tests split per kind and every
 *   service on its own row.
 *
 * The toolbar's expand button opens the whole card in a full-screen dialog.
 * There the chart and its date axis stay pinned at the top and the tests and
 * services scroll beneath them (in Events & Services, everything scrolls).
 *
 * Every row shares one horizontal scale (`timeScale`), so a vertical
 * crosshair through all of them reads as one date.
 */
const TimelineView = ({ scope, metricsLoading = false }: Props) => {
  const theme: Theme = useTheme();
  const plotRef = useRef<HTMLDivElement>(null);

  // What this customer's Timeline looked like last time (per browser), read
  // once on mount; every change below is written back. The anchor date and
  // full screen are deliberately not remembered.
  const [stored] = useState(() => readTimelinePreferences(scope.subjectId));
  const [period, setPeriod] = useState<TimelinePeriod>(stored.period ?? "month");
  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const [view, setView] = useState<TimelineViewMode>(stored.view ?? "combined");
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [targetKeys, setTargetKeys] = useState<string[]>(stored.targetKeys ?? []);
  const [fullscreen, setFullscreen] = useState(false);
  // The service lanes' order, from the toolbar's Services picker.
  const [laneOrder, setLaneOrder] = useState<ServiceLaneKey[]>(
    stored.laneOrder ?? DEFAULT_LANE_ORDER
  );
  // True once the selection is in place; nothing is written back before that.
  const [hydrated, setHydrated] = useState(false);
  const [crosshairPct, setCrosshairPct] = useState<number | null>(null);

  const window = useMemo(() => getTimelineWindow(period, anchor), [period, anchor]);
  const { series, loading: seriesLoading, catalog } = useTimelineSeries(
    selectedKeys,
    window
  );
  const events = useTimelineEvents(scope, window);
  const lanes = useMemo(
    () => orderLanes(events.lanes, laneOrder),
    [events.lanes, laneOrder]
  );
  const laneCounts = useMemo(
    () =>
      Object.fromEntries(events.lanes.map((lane) => [lane.key, lane.bars.length])) as Partial<
        Record<ServiceLaneKey, number>
      >,
    [events.lanes]
  );

  // Seed the opening selection once: what was remembered for this customer,
  // or the latest biomarkers plus Total Burn.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;
    // The tab mounts this view only once the biomarkers have loaded, so the
    // biomarker and wearable catalog is complete here. The PNOĒ metrics come
    // with the report, which may still be loading: a remembered metric key is
    // kept until then (its row appears when the report lands) rather than
    // pruned for being momentarily unknown.
    const remembered = stored.selectedKeys
      ?.filter(
        (key) =>
          catalog.byKey.has(key) ||
          (parseSeriesKey(key).group === "metric" && metricsLoading)
      )
      .slice(0, MAX_TIMELINE_METRICS);
    if (remembered) {
      setSelectedKeys(remembered);
      setTargetKeys((current) => current.filter((key) => remembered.includes(key)));
    } else {
      // A client with no biomarkers still gets the wearable.
      setSelectedKeys([
        ...catalog.recentBiomarkerKeys.slice(0, DEFAULT_BIOMARKERS),
        ...(catalog.byKey.has(DEFAULT_WEARABLE) ? [DEFAULT_WEARABLE] : []),
      ]);
    }
    setHydrated(true);
  }, [catalog.recentBiomarkerKeys, catalog.byKey, stored.selectedKeys, metricsLoading]);

  useEffect(() => {
    if (!hydrated) return;
    writeTimelinePreferences(scope.subjectId, {
      selectedKeys,
      targetKeys,
      view,
      period,
      laneOrder,
    });
  }, [hydrated, scope.subjectId, selectedKeys, targetKeys, view, period, laneOrder]);

  const handleSelectionChange = (keys: string[]) => {
    const capped = keys.slice(0, MAX_TIMELINE_METRICS);
    setSelectedKeys(capped);
    setTargetKeys((current) => current.filter((key) => capped.includes(key)));
  };

  // No goals to draw in a year — wearables aren't charted there.
  const targetOptions = selectedKeys
    .map(parseSeriesKey)
    .filter(
      ({ group, id }) =>
        period !== "year" && group === "wearable" && hasWearableGoal(id)
    )
    .map(({ group, id }) => {
      const key = `${group}:${id}`;
      return { key, label: catalog.byKey.get(key)?.label ?? id };
    });

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = plotRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = event.clientX - rect.left;
    setCrosshairPct(x >= 0 && x <= rect.width ? (x / rect.width) * 100 : null);
  };

  const cssVars = {
    "--timeline-label-width": `${LABEL_WIDTH}px`,
    "--timeline-divider": theme.colors_ds.NEUTRAL_BORDER_WEAK,
    "--timeline-grid": theme.colorTokens.N[40],
  } as CSSProperties;

  const testsByKind = (kind: string) => events.tests.filter((e) => e.kind === kind);

  // The dates of the period. Sits between the metric chart and the event
  // timelines (at the top of Events & Services, which has no chart).
  const axisRow = (
    <div className={`${classes.row} ${classes.rowDivider}`} style={{ height: AXIS_HEIGHT }}>
      <div style={{ flex: `0 0 ${LABEL_WIDTH}px` }} />
      <div className={classes.plot}>
        {window.ticks.map((tick) => (
          <span
            key={tick.t}
            className={classes.axisTick}
            style={{
              left: `${xPct(tick.t, window)}%`,
              top: "50%",
              marginTop: "-8px",
              color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
            }}
          >
            {tick.label}
          </span>
        ))}
      </div>
    </div>
  );

  // In full screen the card fills the dialog. The metrics block grows to the
  // height the toolbar, axis and event rows leave (the selected metrics keep
  // splitting it equally) and the event rows scroll in a ScrollableWrapper
  // capped at half the screen, so the chart is always on top. Normal mode is
  // unchanged: no scroller, the page scrolls.
  const eventsScroll = fullscreen && view !== "chart";
  const gutter = eventsScroll ? SCROLL_GUTTER : 0;
  const scrollable = (rows: React.ReactNode) =>
    eventsScroll ? (
      <div style={styles.eventsScroller(view === "combined")}>
        <ScrollableWrapper>
          {/* One child: the wrapper puts an 8px gap between its children. */}
          <div style={styles.rows(false)}>{rows}</div>
        </ScrollableWrapper>
      </div>
    ) : (
      rows
    );
  const card = (
    <div style={{ ...styles.card(theme, fullscreen), ...cssVars }}>
      <TimelineToolbar
        window={window}
        period={period}
        onPeriodChange={setPeriod}
        onShift={(direction) => setAnchor((a) => shiftAnchor(period, a, direction))}
        onToday={() => setAnchor(new Date())}
        view={view}
        onViewChange={setView}
        catalog={catalog}
        metricsLoading={metricsLoading}
        selectedKeys={selectedKeys}
        onSelectionChange={handleSelectionChange}
        targetOptions={targetOptions}
        targetKeys={targetKeys}
        onTargetChange={setTargetKeys}
        fullscreen={fullscreen}
        onToggleFullscreen={() => setFullscreen((f) => !f)}
        laneOrder={laneOrder}
        laneCounts={laneCounts}
        onLaneOrderChange={setLaneOrder}
      />

      <div
        style={styles.body(fullscreen)}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setCrosshairPct(null)}
      >
        {/* Grid and crosshair sit under / over the plot column of every row. */}
        <div
          ref={plotRef}
          className={classes.overlay}
          style={{ left: LABEL_WIDTH + 1, right: gutter, bottom: 0, zIndex: 0 }}
        >
          {window.gridLines.map((t) => (
            <div key={t} className={classes.gridLine} style={{ left: `${xPct(t, window)}%` }} />
          ))}
        </div>

        <div style={styles.rows(fullscreen)}>
          {hasMetrics(view) ? (
            <>
              <div style={{ ...styles.metrics(fullscreen), paddingRight: gutter }}>
                {selectedKeys.length === 0 && (
                  <div style={styles.emptyMetrics(theme)}>
                    Select up to {MAX_TIMELINE_METRICS} metrics from Filters to chart them here.
                  </div>
                )}
                {series.map((s) =>
                  seriesLoading && s.group === "wearable" && s.points.length === 0 ? (
                    <Skeleton
                      key={s.key}
                      variant="rectangular"
                      sx={styles.metricSkeleton(theme)}
                    />
                  ) : (
                    <MetricRow
                      key={s.key}
                      series={s}
                      window={window}
                      showGoal={targetKeys.includes(s.key)}
                      minHeight={fullscreen ? METRIC_ROW_MIN_HEIGHT : METRIC_ROW_HEIGHT}
                    />
                  )
                )}
              </div>

              <div style={{ paddingRight: gutter }}>{axisRow}</div>

              {view === "combined" &&
                scrollable(
                  <>
                  {events.testsLoading ? (
                    <Skeleton variant="rectangular" sx={styles.rowSkeleton(theme)} />
                  ) : (
                    <TestsRow
                      label="Tests"
                      hint="events"
                      events={events.tests}
                      window={window}
                    />
                  )}

                  {events.servicesLoading ? (
                    <Skeleton variant="rectangular" sx={styles.rowSkeleton(theme)} />
                  ) : lanes.length === 0 ? (
                    <EmptyServicesRow />
                  ) : (
                    lanes.map((lane) => (
                      <ServiceLane key={lane.key} lane={lane} window={window} />
                    ))
                  )}
                  </>
                )}
            </>
          ) : (
            scrollable(
            <>
              {axisRow}
              <div className={classes.row}>
                <div
                  className={`${classes.label} ${classes.labelCompact}`}
                  style={styles.sectionTitle(theme)}
                >
                  Tests &amp; Labs
                </div>
                <div className={classes.plot} />
              </div>
              {events.testsLoading ? (
                <Skeleton variant="rectangular" sx={styles.rowSkeleton(theme)} />
              ) : (
                TEST_ROWS.filter(
                  (row) => !(row.kind === "pnoe" && events.pnoeUnavailable)
                ).map((row) => (
                  <TestsRow
                    key={row.kind}
                    label={row.label}
                    events={testsByKind(row.kind)}
                    window={window}
                    compact
                    divider={false}
                  />
                ))
              )}
              {events.servicesLoading ? (
                <Skeleton variant="rectangular" sx={styles.rowSkeleton(theme)} />
              ) : lanes.length === 0 ? (
                <EmptyServicesRow compact />
              ) : (
                lanes.map((lane) => (
                  <ServiceLane key={lane.key} lane={lane} window={window} expanded />
                ))
              )}
            </>
            )
          )}
        </div>

        {crosshairPct !== null && (
          <div
            className={classes.overlay}
            style={{ left: LABEL_WIDTH + 1, right: gutter, bottom: 0, zIndex: 3 }}
          >
            <div
              className={classes.crosshair}
              style={{
                left: `${crosshairPct}%`,
                backgroundColor: theme.colorTokens.N[60],
              }}
            />
          </div>
        )}
      </div>
    </div>
  );

  if (!fullscreen) return card;
  return (
    <Dialog
      fullScreen
      open
      onClose={() => setFullscreen(false)}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
            // Sideways for the card's min width; vertically the card's own
            // scroller takes over.
            overflowX: "auto",
          },
        },
      }}
    >
      {card}
    </Dialog>
  );
};

export default TimelineView;

/**
 * No service in the window: one row laid out like the Tests row — "Services"
 * in the label column, the message in the plot — so the two line up.
 */
const EmptyServicesRow = ({ compact = false }: { compact?: boolean }) => {
  const theme: Theme = useTheme();
  return (
    <div
      className={classes.row}
      style={{ height: compact ? COMPACT_ROW_HEIGHT : TESTS_ROW_HEIGHT }}
    >
      <div className={`${classes.label} ${compact ? classes.labelCompact : ""}`}>
        <div style={styles.emptyServicesName(theme, compact)}>Services</div>
      </div>
      <div className={classes.plot} style={styles.emptyServicesPlot}>
        <span style={styles.emptyServicesText(theme)}>
          No services in this period
        </span>
      </div>
    </div>
  );
};

const styles = {
  card: (theme: Theme, fullscreen: boolean) => ({
    display: "flex",
    flexDirection: "column" as "column",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    border: fullscreen ? "none" : `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: fullscreen ? 0 : "8px",
    padding: "20px 16px 16px",
    ...(fullscreen && {
      height: "100%",
      boxSizing: "border-box" as "border-box",
      minWidth: FULLSCREEN_MIN_WIDTH,
    }),
  }),
  body: (fullscreen: boolean) => ({
    position: "relative" as "relative",
    display: "flex",
    flexDirection: "column" as "column",
    // Exactly the screen: the event rows scroll inside it.
    ...(fullscreen && { flex: "1 1 auto", minHeight: 0 }),
  }),
  rows: (fullscreen: boolean) => ({
    position: "relative" as "relative",
    zIndex: 2,
    display: "flex",
    flexDirection: "column" as "column",
    ...(fullscreen && { flex: "1 1 auto", minHeight: 0 }),
  }),
  // Full screen: the event rows scroll. Under the chart they take their own
  // height up to half the screen and the metrics block gets the rest; in
  // Events & Services they have the whole screen.
  eventsScroller: (underChart: boolean) => ({
    flex: underChart ? "0 1 auto" : "1 1 auto",
    minHeight: 0,
    ...(underChart && { maxHeight: "50vh" }),
    display: "flex",
    flexDirection: "column" as "column",
  }),
  // A floor, not a fixed height, so five rows with a wrapped name can still
  // grow; with fewer rows each one stretches to its equal share. In full
  // screen the block is at least half the screen and takes whatever height
  // the other rows leave beyond that — but never more than 80% of the area
  // under the toolbar, so on a short window the events stay in view.
  metrics: (fullscreen: boolean) => ({
    display: "flex",
    flexDirection: "column" as "column",
    minHeight: fullscreen
      ? `min(max(${METRICS_BLOCK_HEIGHT}px, 50vh), 80%)`
      : METRICS_BLOCK_HEIGHT,
    ...(fullscreen && { flex: "1 1 auto" }),
  }),
  sectionTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "13px",
    lineHeight: "18px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  emptyMetrics: (theme: Theme) => ({
    // Fills the metrics block, so the Tests row stays where it is.
    flex: "1 1 0",
    display: "flex",
    alignItems: "center",
    padding: "24px 12px",
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    borderBottom: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
  }),
  emptyServicesName: (theme: Theme, compact: boolean) => ({
    fontFamily: compact ? "var(--font-Regular)" : "var(--font-Medium)",
    fontSize: compact ? "11px" : "14px",
    lineHeight: compact ? "16px" : "20px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  emptyServicesPlot: {
    display: "flex",
    alignItems: "center",
    padding: "0 12px",
  },
  emptyServicesText: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  rowSkeleton: (theme: Theme) => ({
    height: "52px",
    margin: "4px 0",
    borderRadius: "8px",
    backgroundColor: theme.colorTokens.N[30],
  }),
  // A loading metric takes the same share of the block as the row it stands
  // in for, so nothing jumps when the readings arrive.
  metricSkeleton: (theme: Theme) => ({
    flex: "1 1 0",
    minHeight: `${METRIC_ROW_HEIGHT - 8}px`,
    margin: "4px 0",
    borderRadius: "8px",
    backgroundColor: theme.colorTokens.N[30],
  }),
};
